import { useEffect, useRef, useState } from 'react'

import type { CaptureSession } from '../../api/types.gen'
import { Skeleton } from '../../ui'

/**
 * El formulario de tarjeta de Izipay, embebido.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Los campos son de Izipay, no nuestros
 * ─────────────────────────────────────────────────────────────────────────────
 * Lo que se pinta aquí dentro son iframes servidos por la pasarela. El número de
 * tarjeta y el CVV se teclean DENTRO de ellos y viajan de ahí a Izipay: nunca
 * pasan por este dominio ni por nuestro servidor. Es lo único que mantiene el
 * alcance PCI en SAQ-A, y lo que se rompería el día que alguien sustituyera esto
 * por un `<input>` propio «para que combine mejor».
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * De aquí sale una respuesta firmada, y no se toca
 * ─────────────────────────────────────────────────────────────────────────────
 * Al registrar la tarjeta, Izipay devuelve un JSON firmado. Este componente lo
 * pasa **tal cual llegó** —`rawClientAnswer`, no el objeto ya parseado— porque la
 * firma se calculó sobre ese texto exacto: volver a serializarlo cambia espacios,
 * orden y escapes, y el servidor rechazaría una respuesta legítima.
 *
 * Dónde vive el token dentro de ese JSON no se mira aquí. Lo saca el backend, que
 * es donde hay tests: si la pasarela mueve el campo, el fallo tiene que salir en
 * un sitio que se prueba y no en este archivo.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Sin verificar contra Izipay
 * ─────────────────────────────────────────────────────────────────────────────
 * Escrito contra su documentación, sin credenciales todavía: nada de esto ha
 * llegado a pintarse contra la pasarela real. Lo que más probablemente necesite
 * un ajuste al estrenarlo son las rutas de los recursos y el nombre del tema.
 */

/** Base de los recursos del cliente JavaScript, colgando de `endpoint`. */
const RECURSOS = '/static/js/krypton-client/V4.0'

/** El contenedor donde la pasarela inyecta sus iframes. */
const ID = 'izipay-card-form'

interface KryptonSubmitEvent {
  /** El JSON tal como lo firmó la pasarela. NO reserializar. */
  rawClientAnswer: string
  hash: string
}

interface Krypton {
  setFormConfig(config: Record<string, unknown>): Promise<unknown>
  onSubmit(handler: (event: KryptonSubmitEvent) => boolean): Promise<unknown>
  attachForm(selector: string): Promise<{ KR: Krypton; result: { formId: string } }>
  showForm(formId: string): Promise<unknown>
  removeForms(): Promise<unknown>
}

declare global {
  interface Window {
    KR?: Krypton
  }
}

export function IzipayCardForm({
  session,
  onCaptured,
  onFailure,
}: {
  session: CaptureSession
  /** Los dos opacos: el cuerpo firmado y su firma. */
  onCaptured: (answer: string, hash: string) => void
  onFailure: (message: string) => void
}) {
  const [listo, setListo] = useState(false)

  /*
   * Los callbacks van por referencia y no por dependencia del efecto.
   *
   * Sin esto, cada render del padre —uno por tecla en cualquier campo de la
   * página— crearía funciones nuevas, el efecto se volvería a lanzar y el
   * formulario se desmontaría y remontaría con el usuario escribiendo dentro.
   */
  const capturado = useRef(onCaptured)
  const fallo = useRef(onFailure)

  // Se ponen al día en un efecto sin dependencias —después de cada render— y no
  // durante el render: tocar una ref mientras se pinta es lo que React prohíbe,
  // porque el render puede descartarse y dejar la ref adelantada respecto a lo
  // que hay en pantalla.
  useEffect(() => {
    capturado.current = onCaptured
    fallo.current = onFailure
  })

  useEffect(() => {
    let vivo = true

    const montar = async () => {
      const KR = await cargarKrypton(session.endpoint, session.public_key)
      if (!vivo) return

      await KR.setFormConfig({
        formToken: session.form_token,
        'kr-language': 'es-PE',
      })

      await KR.onSubmit((event) => {
        capturado.current(event.rawClientAnswer, event.hash)

        // `false` corta el POST que la pasarela haría por su cuenta. Esto es una
        // SPA: la navegación la decide el padre después de activar la cuenta.
        return false
      })

      const { KR: instancia, result } = await KR.attachForm(`#${ID}`)
      if (!vivo) return

      await instancia.showForm(result.formId)
      setListo(true)
    }

    montar().catch(() => {
      if (!vivo) return

      /*
       | Esto es casi siempre la red, un bloqueador de scripts o un `formToken`
       | caducado. Se dice sin culpar a la tarjeta: todavía no hay ninguna, y
       | sugerir que el banco rechazó algo haría que el estudio llame a su banco
       | por un problema que no es suyo.
       */
      fallo.current('No se pudo cargar el formulario de pago. Revisa tu conexión y vuelve a intentarlo.')
    })

    return () => {
      vivo = false
      // Sin esto, volver a esta pantalla encuentra los iframes de la vez
      // anterior y `attachForm` monta un segundo formulario encima.
      void window.KR?.removeForms()
    }
  }, [session.endpoint, session.public_key, session.form_token])

  return (
    <div>
      {!listo && <Skeleton className="h-64 w-full rounded-card" />}
      <div id={ID} className={listo ? 'kr-smart-form' : 'hidden'} />
    </div>
  )
}

/**
 * Carga el cliente de la pasarela una sola vez por pestaña.
 *
 * `KR` es un objeto global suyo y no admite dos instancias: si ya está cargado,
 * se reutiliza y sólo se le cambia la configuración. Cargar el script otra vez no
 * daría un segundo cliente, daría un cliente en un estado a medias.
 */
let cargando: Promise<Krypton> | null = null

function cargarKrypton(endpoint: string, publicKey: string): Promise<Krypton> {
  if (window.KR) return Promise.resolve(window.KR)
  if (cargando) return cargando

  cargando = (async () => {
    // El tema, primero: sus estilos tienen que estar cuando el cliente pinte, y
    // si llegan después el formulario aparece un instante sin formato.
    cargarHoja(`${endpoint}${RECURSOS}/ext/classic-reset.css`)

    // La clave pública va como ATRIBUTO del script y la lee al arrancar, así que
    // no vale ponerla después.
    await cargarScript(`${endpoint}${RECURSOS}/stable/kr-payment-form.min.js`, {
      'kr-public-key': publicKey,
    })
    await cargarScript(`${endpoint}${RECURSOS}/ext/classic.js`)

    if (!window.KR) throw new Error('El cliente de la pasarela cargó sin exponerse.')

    return window.KR
  })()

  // Un fallo no puede quedar memorizado: dejaría la pantalla rota hasta recargar,
  // cuando lo que suele pasar es que la red volvió.
  cargando.catch(() => {
    cargando = null
  })

  return cargando
}

function cargarHoja(href: string): void {
  if (document.querySelector(`link[href="${href}"]`)) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.append(link)
}

function cargarScript(src: string, attrs: Record<string, string> = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const existente = document.querySelector(`script[src="${src}"]`)
    if (existente) {
      resolve()

      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = false
    for (const [nombre, valor] of Object.entries(attrs)) script.setAttribute(nombre, valor)
    script.addEventListener('load', () => {
      resolve()
    })
    script.addEventListener('error', () => {
      // Se quita del DOM para que el siguiente intento vuelva a pedirlo en vez
      // de encontrarse la etiqueta muerta y darla por buena.
      script.remove()
      reject(new Error(`No se pudo cargar ${src}`))
    })
    document.head.append(script)
  })
}
